import { useCallback, useEffect, useRef, useState } from "react";
import {
  INSTAGRAM_OAUTH_ERROR_TYPE,
  INSTAGRAM_OAUTH_SUCCESS_TYPE,
  instagramOauthPopupMessageSchema,
  type InstagramOauthPopupMessage,
} from "@multi-chat/shared";
import { API_BASE_URL } from "../../api/client.js";
import { instagramConnectStartUrl } from "../../api/instagram-api.js";
import { openCenteredPopup } from "../../lib/open-centered-popup.js";

export type InstagramPopupStatus =
  | { kind: "idle" }
  | { kind: "opening" }
  | { kind: "blocked" }
  | { kind: "closed" }
  | { kind: "success" }
  | { kind: "error"; code: string };

interface UseInstagramPopupResult {
  status: InstagramPopupStatus;
  start: (organizationId: number) => void;
  reset: () => void;
}

const POPUP_NAME = "multi_chat_instagram_connect";
const POPUP_SIZE = { width: 600, height: 750 };

/** Callback HTML is served by the API; only accept postMessage from that origin. */
export function isAllowedInstagramOauthOrigin(origin: string, apiBaseUrl = API_BASE_URL): boolean {
  try {
    return new URL(origin).origin === new URL(apiBaseUrl).origin;
  } catch {
    return false;
  }
}

export function parseInstagramOauthPopupMessage(
  data: unknown,
): InstagramOauthPopupMessage | null {
  const parsed = instagramOauthPopupMessageSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

export function useInstagramPopup(onSuccess: () => void): UseInstagramPopupResult {
  const [status, setStatus] = useState<InstagramPopupStatus>({ kind: "idle" });
  const popupRef = useRef<Window | null>(null);
  const closedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearClosedTimer = useCallback(() => {
    if (closedTimerRef.current) {
      clearInterval(closedTimerRef.current);
      closedTimerRef.current = null;
    }
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!isAllowedInstagramOauthOrigin(event.origin)) return;
      const message = parseInstagramOauthPopupMessage(event.data);
      if (!message) return;

      if (message.type === INSTAGRAM_OAUTH_SUCCESS_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current?.close();
        popupRef.current = null;
        setStatus({ kind: "success" });
        onSuccessRef.current();
      } else if (message.type === INSTAGRAM_OAUTH_ERROR_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current?.close();
        popupRef.current = null;
        setStatus({ kind: "error", code: message.code });
      }
    },
    [clearClosedTimer],
  );

  const reset = useCallback(() => {
    clearClosedTimer();
    window.removeEventListener("message", handleMessage);
    popupRef.current?.close();
    popupRef.current = null;
    setStatus({ kind: "idle" });
  }, [clearClosedTimer, handleMessage]);

  const start = useCallback(
    (organizationId: number) => {
      setStatus({ kind: "opening" });
      window.addEventListener("message", handleMessage);

      const url = `${API_BASE_URL}${instagramConnectStartUrl(organizationId)}`;
      const popup = openCenteredPopup(url, POPUP_NAME, POPUP_SIZE);

      if (!popup) {
        window.removeEventListener("message", handleMessage);
        setStatus({ kind: "blocked" });
        return;
      }

      popupRef.current = popup;

      clearClosedTimer();
      closedTimerRef.current = setInterval(() => {
        if (popup.closed) {
          clearClosedTimer();
          window.removeEventListener("message", handleMessage);
          popupRef.current = null;
          setStatus((current) =>
            current.kind === "success" || current.kind === "error"
              ? current
              : { kind: "closed" },
          );
        }
      }, 500);
    },
    [handleMessage, clearClosedTimer],
  );

  useEffect(() => {
    return () => {
      clearClosedTimer();
      window.removeEventListener("message", handleMessage);
    };
  }, [clearClosedTimer, handleMessage]);

  return { status, start, reset };
}
